import React, { useState } from 'react';
import { X, Target, Save, Zap, Flame, Trophy, Calendar } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const QUICK_TEMPLATES = [
    { title: '100 km Mensuales', sport: 'Ciclismo', metric: 'distance', timeframe: 'monthly', target: 100, unit: 'km' },
    { title: '5 Salidas en Bici / Semana', sport: 'Ciclismo', metric: 'sessions', timeframe: 'weekly', target: 5, unit: 'count' },
    { title: '50 km Running / Mes', sport: 'Running', metric: 'distance', timeframe: 'monthly', target: 50, unit: 'km' },
    { title: '1.000m D+ Desnivel / Mes', sport: 'Cualquiera', metric: 'elevation', timeframe: 'monthly', target: 1000, unit: 'm' },
    { title: '4 Entrenos de Fuerza / Semana', sport: 'Fuerza', metric: 'sessions', timeframe: 'weekly', target: 4, unit: 'count' },
];

const GoalCreatorModal = ({ onClose, onGoalCreated, initialGoal }) => {
    const [sport, setSport] = useState(initialGoal?.sport || 'Ciclismo');
    const [metric, setMetric] = useState(initialGoal?.metric || 'distance');
    const [timeframe, setTimeframe] = useState(initialGoal?.timeframe || 'monthly');
    const [target, setTarget] = useState(initialGoal?.target || 100);
    const [isSaving, setIsSaving] = useState(false);

    const applyTemplate = (tpl) => {
        setSport(tpl.sport);
        setMetric(tpl.metric);
        setTimeframe(tpl.timeframe);
        setTarget(tpl.target);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const res = await fetch(`${API_URL}/goals`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    id: initialGoal?.id,
                    sport,
                    metric,
                    timeframe,
                    target: Number(target),
                    unit: metric === 'distance' ? 'km' : (metric === 'sessions' ? 'count' : metric === 'elevation' ? 'm' : 'unit')
                })
            });
            const data = await res.json();
            if (res.ok) {
                onGoalCreated(data.goals);
                onClose();
            } else {
                alert('Error al guardar: ' + (data.error || 'Desconocido'));
            }
        } catch (err) {
            console.error(err);
            alert('Error de conexión');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(5, 10, 20, 0.8)', backdropFilter: 'blur(10px)', padding: '1rem'
        }}>
            <div style={{
                width: '100%', maxWidth: '520px', padding: '2rem', borderRadius: '24px', position: 'relative',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.1)', background: '#0f172a'
            }}>
                <button onClick={onClose} style={{
                    position: 'absolute', top: '1.25rem', right: '1.25rem', background: 'rgba(255,255,255,0.06)',
                    border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', borderRadius: '50%', padding: '0.5rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
                }}>
                    <X size={20} />
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
                    <div style={{ padding: '8px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', display: 'flex' }}>
                        <Target size={24} />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#ffffff' }}>
                            {initialGoal ? 'Editar Reto / Objetivo' : 'Nuevo Reto Deportivo'}
                        </h2>
                        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                            Configura tu próximo hito de entrenamiento
                        </span>
                    </div>
                </div>

                {/* Plantillas Rápidas */}
                {!initialGoal && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#10b981', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Zap size={14} /> Plantillas de Reto Populares
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
                            {QUICK_TEMPLATES.map((tpl, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => applyTemplate(tpl)}
                                    style={{
                                        whiteSpace: 'nowrap',
                                        background: 'rgba(255,255,255,0.04)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '10px',
                                        padding: '0.4rem 0.75rem',
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    ⚡ {tpl.title}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Deporte / Actividad</label>
                        <select 
                            value={sport} 
                            onChange={e => setSport(e.target.value)}
                            style={{ padding: '0.75rem 0.9rem', borderRadius: '10px', background: 'rgba(0,0,0,0.4)', color: 'white', border: '1px solid rgba(255,255,255,0.12)' }}
                        >
                            <option value="Cualquiera">Cualquier Actividad</option>
                            <option value="Ciclismo">Ciclismo</option>
                            <option value="Running">Running</option>
                            <option value="Natación">Natación</option>
                            <option value="Pádel">Pádel / Tenis</option>
                            <option value="Fuerza">Fuerza / Pesas</option>
                            <option value="CrossFit">CrossFit / Funcional</option>
                            <option value="Pilates">Pilates / Yoga</option>
                            <option value="Fútbol">Fútbol / Equipo</option>
                            <option value="Caminar">Caminar</option>
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Tipo de Métrica</label>
                            <select 
                                value={metric} 
                                onChange={e => setMetric(e.target.value)}
                                style={{ padding: '0.75rem 0.9rem', borderRadius: '10px', background: 'rgba(0,0,0,0.4)', color: 'white', border: '1px solid rgba(255,255,255,0.12)' }}
                            >
                                <option value="distance">Distancia (km)</option>
                                <option value="sessions">Número de Salidas / Sesiones</option>
                                <option value="elevation">Desnivel Acumulado (m)</option>
                                <option value="duration">Tiempo Total (horas)</option>
                                <option value="calories">Calorías</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Plazo Temporal</label>
                            <select 
                                value={timeframe} 
                                onChange={e => setTimeframe(e.target.value)}
                                style={{ padding: '0.75rem 0.9rem', borderRadius: '10px', background: 'rgba(0,0,0,0.4)', color: 'white', border: '1px solid rgba(255,255,255,0.12)' }}
                            >
                                <option value="weekly">Semanal</option>
                                <option value="monthly">Mensual</option>
                                <option value="annual">Anual</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                            Objetivo ({metric === 'distance' ? 'km' : metric === 'sessions' ? 'sesiones' : metric === 'elevation' ? 'metros' : 'unidades'})
                        </label>
                        <input 
                            type="number" 
                            min="1"
                            value={target} 
                            onChange={e => setTarget(e.target.value)}
                            style={{ padding: '0.75rem', borderRadius: '10px', background: 'rgba(0,0,0,0.4)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)', fontSize: '1.25rem', fontWeight: 700, textAlign: 'center' }}
                            required
                        />
                    </div>

                    <button 
                        type="submit" 
                        disabled={isSaving}
                        className="btn-primary"
                        style={{
                            marginTop: '0.75rem',
                            padding: '0.85rem',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            border: 'none',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            justify: 'center',
                            alignItems: 'center',
                            gap: '0.5rem',
                            boxShadow: '0 8px 20px rgba(16, 185, 129, 0.25)'
                        }}
                    >
                        {isSaving ? 'Guardando Reto...' : <><Save size={18} /> Guardar Reto</>}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default GoalCreatorModal;
